import React, { ChangeEvent } from 'react';
import { Input, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DSBox from '../../../../../design-system/DSBox';
import DSPaper from '../../../../../design-system/Paper/DSPaper';
import DSValueTypography from '../../../../../design-system/Typography/DSValueTypography';
import useInputsDragAndDrop, {
  createSyntheticFileEvent,
} from '../../../../Klydos/hooks/useInputsDragAndDrop';
import { createFileValidation } from '../../../../../utils';

type CollectionAssetFileInputEmptyProps = {
  fileSpecsText: string;
  acceptedFormats: string;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const CollectionAssetFileInputEmpty = ({
  fileSpecsText,
  acceptedFormats,
  onFileChange,
}: CollectionAssetFileInputEmptyProps) => {
  const { spacing, palette } = useTheme();

  const validation = createFileValidation(acceptedFormats);

  const { isDragging, dragHandlers, handleInputChange } = useInputsDragAndDrop({
    onFileAccepted: (file: File) => {
      const syntheticEvent = createSyntheticFileEvent(file);
      onFileChange(syntheticEvent);
    },
    validation,
    enabled: true,
  });
  return (
    <DSBox component="label" sx={{ width: '100%', cursor: 'pointer' }}>
      <DSPaper
        {...dragHandlers}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          aspectRatio: '1',
          padding: spacing(4),
          justifyContent: 'center',
          alignItems: 'center',
          gap: spacing(2),
          alignSelf: 'stretch',
          borderRadius: spacing(3),
          border: `2px dashed ${isDragging ? palette.primary.main : palette.white[12]}`,
          background: isDragging ? `${palette.primary.main}10` : 'transparent',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: isDragging
              ? `${palette.primary.main}20`
              : palette.white[5],
            borderColor: isDragging ? palette.primary.main : palette.white[20],
            '& .add-icon': {
              color: palette.white[90],
            },
          },
        }}
      >
        <AddIcon
          className="add-icon"
          sx={{
            color: palette.white[70],
            fontSize: '48px',
            transition: 'color 0.3s ease',
          }}
        />
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing(0.5),
          }}
        >
          {fileSpecsText.split('\n').map((line, index) => (
            <DSValueTypography
              key={index}
              color={palette.gray[119]}
              sx={{
                textAlign: 'center',
              }}
            >
              {line}
            </DSValueTypography>
          ))}
          {validation.maxSizeBytes && (
            <DSValueTypography
              color={palette.gray[119]}
              sx={{ textAlign: 'center' }}
            >
              Max {validation.maxSizeBytes / (1024 * 1024)}MB
            </DSValueTypography>
          )}
        </DSBox>
        <Input
          inputProps={{ accept: acceptedFormats }}
          type="file"
          onChange={handleInputChange}
          style={{
            display: 'none',
          }}
        />
      </DSPaper>
    </DSBox>
  );
};

export default CollectionAssetFileInputEmpty;
